import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw, X } from "lucide-react";
import "./runtime.css";

export const REFRESH_INTERVAL = 5_000;
export const CONFIG_REFRESH_INTERVAL = 30_000;

export type SortDirection = "asc" | "desc";

export function useRuntimeSort<Key extends string>(initialKey: Key) {
  const [sort, setSort] = useState<{ key: Key; direction: SortDirection }>({ key: initialKey, direction: "desc" });
  const sortBy = (key: Key, initialDirection: SortDirection = "desc") => setSort((current) => ({
    key,
    direction: current.key === key ? current.direction === "desc" ? "asc" : "desc" : initialDirection,
  }));
  return { sort, sortBy };
}

export function SortableHeader({ label, selected, direction, onSort, numeric = false, initialDirection = "desc" }: {
  label: string; selected: boolean; direction: SortDirection; onSort: () => void; numeric?: boolean; initialDirection?: SortDirection;
}) {
  const Icon = selected ? direction === "asc" ? ArrowUp : ArrowDown : ArrowUpDown;
  const nextDirection = selected ? direction === "desc" ? "asc" : "desc" : initialDirection;
  return <th scope="col" aria-sort={selected ? direction === "asc" ? "ascending" : "descending" : "none"}
    className={`${numeric ? "runtime-numeric " : ""}${selected ? "runtime-sorted" : ""}`}>
    <button type="button" className="runtime-sort-button" onClick={onSort} title={`按${label}${nextDirection === "asc" ? "升序" : "降序"}排序`}>
      {label}<Icon size={14} aria-hidden="true" />
    </button>
  </th>;
}

export function usePageVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  return visible;
}

export function sampleTime(value?: string) {
  if (!value) return "尚未读取";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "采样时间未知" : date.toLocaleTimeString("zh-CN", { hour12: false });
}

export function RuntimeRefresh({ sampledAt, fetching, error, onRefresh }: {
  sampledAt?: string; fetching: boolean; error: Error | null; onRefresh: () => void;
}) {
  return <div className="runtime-refresh">
    <span role="status">{error ? "更新失败 · " : ""}统计采样 {sampleTime(sampledAt)}<small>统计每 5 秒 · 配置每 30 秒更新</small></span>
    <button type="button" className="secondary-button compact" onClick={onRefresh} disabled={fetching}>
      <RefreshCw size={14} />{fetching ? "刷新中" : "刷新"}
    </button>
  </div>;
}

export function RuntimeError({ error, retry }: { error?: Error | null; retry: () => void }) {
  if (!error) return null;
  return <div className="runtime-error" role="alert"><span>读取失败：{error.message}。已有数据保留至上次成功采样。</span><button type="button" onClick={retry}>重试</button></div>;
}

export function RuntimeSkeleton({ rows = 5 }: { rows?: number }) {
  return <div className="runtime-skeleton" aria-busy="true" aria-label="正在读取统计">
    {Array.from({ length: rows }, (_, index) => <span key={index} />)}
  </div>;
}

export function Count({ value, loading = false }: { value: number | null | undefined; loading?: boolean }) {
  if (loading) return <span className="runtime-number-skeleton" aria-label="正在读取数量" />;
  return <span className="runtime-count">{value == null ? "—" : value.toLocaleString("zh-CN")}</span>;
}

export function RuntimeDrawer({ title, subtitle, children, onClose }: {
  title: string; subtitle: string; children: ReactNode; onClose: () => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const originalFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    close.current?.focus();
    return () => { document.body.style.overflow = overflow; originalFocus?.focus(); };
  }, []);
  return <div className="runtime-drawer-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={panel} className="runtime-drawer" role="dialog" aria-modal="true" aria-labelledby="runtime-drawer-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") { event.stopPropagation(); onClose(); }
        if (event.key !== "Tab") return;
        const targets = panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input, select, [tabindex="0"]');
        const first = targets?.[0]; const last = targets?.[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}>
      <div className="runtime-drawer-header"><div><h2 id="runtime-drawer-title">{title}</h2><p>{subtitle}</p></div><button ref={close} type="button" className="icon-button" onClick={onClose} aria-label="关闭统计抽屉"><X size={20} /></button></div>
      <div className="runtime-drawer-body">{children}</div>
    </section>
  </div>;
}

export const stateLabels = {
  queued: "排队中", waiting_capacity: "等待名额", dispatching: "提交中", running: "运行中", postprocessing: "后处理中",
};
export type RuntimeState = keyof typeof stateLabels;

export function StatusDistributionBar({ label, counts, maximum }: {
  label: string; counts: Partial<Record<RuntimeState, number>>; maximum: number;
}) {
  const entries = (Object.entries(counts) as [RuntimeState, number][]);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  return <div className="runtime-distribution-row">
    <div className="runtime-distribution-heading"><strong>{label}</strong><span>{total.toLocaleString()} 条</span></div>
    <div className="runtime-status-track" role="img" aria-label={`${label}：${entries.map(([key, count]) => `${stateLabels[key]} ${count}`).join("，")}`}>
      {entries.map(([key, count]) => <span key={key} className={`runtime-state-${key}`} style={{ width: `${maximum ? count / maximum * 100 : 0}%` }} title={`${stateLabels[key]} ${count}`} />)}
    </div>
    <div className="runtime-status-values">{entries.map(([key, count]) => <span key={key}><i className={`runtime-state-${key}`} />{stateLabels[key]} <b>{count.toLocaleString()}</b></span>)}</div>
  </div>;
}

export function CapacityMeter({ active, limit }: { active: number | null | undefined; limit: number }) {
  const percentage = active != null && limit > 0 ? active / limit * 100 : null;
  return <div className="runtime-capacity">
    <span>{percentage == null ? "—" : `${percentage.toFixed(1)}%`}</span>
    <div className="runtime-capacity-track" aria-hidden="true"><i className={percentage != null && percentage >= 100 ? "full" : ""} style={{ width: `${percentage == null ? 0 : Math.min(100, percentage)}%` }} /></div>
  </div>;
}
